import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import VenueCard from './VenueCard';

const VenueList = ({ venues, onVenueSelect, onVenueBooking, selectedVenue, isLoading, filters }) => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState(filters?.sortBy || 'distance');

  const sortOptions = [
    { value: 'distance', label: 'Distância', icon: 'MapPin' },
    { value: 'price_low', label: 'Menor Preço', icon: 'TrendingDown' },
    { value: 'price_high', label: 'Maior Preço', icon: 'TrendingUp' },
    { value: 'rating', label: 'Melhor Avaliação', icon: 'Star' },
    { value: 'availability', label: 'Disponibilidade', icon: 'Clock' }
  ];

  const getSortedVenues = () => {
    const sorted = [...venues];
    
    switch (sortBy) {
      case 'distance':
        return sorted?.sort((a, b) => (a?.distance || 0) - (b?.distance || 0));
      case 'price_low':
        return sorted?.sort((a, b) => a?.pricePerHour - b?.pricePerHour);
      case 'price_high':
        return sorted?.sort((a, b) => b?.pricePerHour - a?.pricePerHour);
      case 'rating':
        return sorted?.sort((a, b) => b?.rating - a?.rating);
      case 'availability':
        return sorted?.sort((a, b) => {
          const availabilityOrder = { 'available': 0, 'busy': 1, 'unavailable': 2 };
          return availabilityOrder?.[a?.availability] - availabilityOrder?.[b?.availability];
        });
      default:
        return sorted;
    }
  };

  const sortedVenues = getSortedVenues();

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Loading Header */}
        <div className="flex items-center justify-between">
          <div className="h-6 bg-muted rounded w-32 animate-pulse"></div>
          <div className="h-8 bg-muted rounded w-24 animate-pulse"></div>
        </div>
        {/* Loading Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[...Array(6)]?.map((_, index) => (
            <div key={index} className="bg-card border border-border rounded-lg p-4 animate-pulse">
              <div className="h-48 bg-muted rounded-lg mb-4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
                <div className="flex space-x-2">
                  <div className="h-6 bg-muted rounded w-16"></div>
                  <div className="h-6 bg-muted rounded w-20"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (venues?.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="MapPin" size={24} className="text-muted-foreground" />
        </div>
        <h3 className="font-heading font-semibold text-foreground mb-2">
          Nenhuma quadra encontrada
        </h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          Não encontramos quadras que atendam aos seus critérios de busca. 
          Tente ajustar os filtros ou expandir a área de busca.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="outline"
            onClick={() => window.location?.reload()}
            iconName="RotateCcw"
            iconPosition="left"
            iconSize={16}
          >
            Limpar Filtros
          </Button>
          <Button
            variant="default"
            onClick={() => {/* Expand search radius */}}
            iconName="Search"
            iconPosition="left"
            iconSize={16}
          >
            Expandir Busca
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* List Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h2 className="font-heading font-semibold text-foreground">
            {venues?.length} quadra{venues?.length !== 1 ? 's' : ''} encontrada{venues?.length !== 1 ? 's' : ''}
          </h2>
          
          {filters?.location && (
            <div className="flex items-center space-x-1 text-sm text-muted-foreground">
              <Icon name="MapPin" size={14} />
              <span>em {filters?.location}</span>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {/* Sort Dropdown */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e?.target?.value)}
              className="appearance-none bg-card border border-border rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            >
              {sortOptions?.map((option) => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
            <Icon 
              name="ChevronDown" 
              size={14} 
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
            />
          </div>

          {/* View Mode Toggle */}
          <div className="hidden md:flex border border-border rounded-lg p-1">
            <Button
              variant={viewMode === 'grid' ? "default" : "ghost"}
              size="xs"
              onClick={() => setViewMode('grid')}
              iconName="Grid3X3"
              iconSize={14}
            />
            <Button
              variant={viewMode === 'list' ? "default" : "ghost"}
              size="xs"
              onClick={() => setViewMode('list')}
              iconName="List"
              iconSize={14}
            />
          </div>
        </div>
      </div>
      {/* Venue Grid/List */}
      <div className={`grid gap-4 ${
        viewMode === 'grid' ?'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3' :'grid-cols-1'
      }`}>
        {sortedVenues?.map((venue) => (
          <VenueCard
            key={venue?.id}
            venue={venue}
            onSelect={onVenueSelect}
            onBooking={onVenueBooking}
            isSelected={selectedVenue?.id === venue?.id}
          />
        ))}
      </div>
      {/* Load More Button */}
      {venues?.length >= 20 && (
        <div className="text-center pt-6">
          <Button
            variant="outline"
            onClick={() => {/* Load more venues */}}
            iconName="ChevronDown"
            iconPosition="right"
            iconSize={16}
          >
            Carregar Mais Quadras
          </Button>
        </div>
      )}
      {/* Results Summary */}
      <div className="text-center text-sm text-muted-foreground pt-4 border-t border-border">
        <p>
          Mostrando {venues?.length} de {venues?.length} quadras disponíveis
          {filters?.radius && ` em um raio de ${filters?.radius}km`}
        </p>
      </div>
    </div>
  );
};

export default VenueList;